export default function FamilyDashboard() {
  return (
    <section id="family-dashboard" className="bg-background-50 py-16 md:py-24 px-4 md:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-primary-500/10 rounded-full px-3 py-1.5 mb-5">
              <i className="ri-group-line text-primary-500 text-xs"></i>
              <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider">For Families</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-5 leading-tight">
              Give families and caregivers one shared view
            </h2>
            <p className="text-base text-foreground-600/80 leading-relaxed mb-8">
              Family members and caregivers can see important updates in one place, helping everyone stay informed and coordinated.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: 'ri-medicine-bottle-line', label: 'View medication status' },
                { icon: 'ri-restaurant-line', label: 'Track meals and hydration' },
                { icon: 'ri-stethoscope-line', label: 'See upcoming appointments' },
                { icon: 'ri-calendar-event-line', label: 'Schedule family visits' },
                { icon: 'ri-file-text-line', label: 'Add caregiver notes' },
                { icon: 'ri-notification-3-line', label: 'Receive missed-task alerts' },
                { icon: 'ri-line-chart-line', label: 'Monitor wellbeing trends' },
                { icon: 'ri-chat-3-line', label: 'Communicate with the care circle' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-background-200/70">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary-500/10 rounded-lg shrink-0">
                    <i className={`${item.icon} text-primary-500 text-sm`}></i>
                  </div>
                  <span className="text-sm text-foreground-700/80">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-[280px] md:w-[320px] bg-primary-500 rounded-[3rem] p-3 shadow-2xl shadow-primary-500/20">
                <div className="bg-white rounded-[2.5rem] overflow-hidden">
                  <div className="bg-primary-500 h-6 flex items-center justify-center rounded-t-[2.5rem]">
                    <div className="w-16 h-1 bg-white/30 rounded-full"></div>
                  </div>
                  <div className="p-5 pb-8">
                    <div className="text-center mb-4">
                      <div className="w-8 h-8 mx-auto bg-primary-500/10 rounded-lg flex items-center justify-center mb-2">
                        <i className="ri-group-line text-primary-500 text-sm"></i>
                      </div>
                      <h4 className="text-sm font-semibold text-foreground-950">Family Dashboard</h4>
                      <p className="text-xs text-foreground-600/50">CareBridge for Mom</p>
                    </div>
                    <div className="space-y-3">
                      <div className="bg-primary-500/5 rounded-xl p-3 border border-primary-500/10">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-medicine-bottle-line text-primary-500 text-sm"></i>
                          <span className="text-xs font-semibold text-foreground-950">Medication Status</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-secondary-500 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-[10px]"></i>
                          </div>
                          <span className="text-xs text-foreground-600/60">Morning dose taken at 8:15 AM</span>
                        </div>
                      </div>
                      <div className="bg-secondary-500/5 rounded-xl p-3 border border-secondary-500/10">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-restaurant-line text-secondary-500 text-sm"></i>
                          <span className="text-xs font-semibold text-foreground-950">Today&apos;s Meals</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-secondary-500 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-white text-[10px]"></i>
                          </div>
                          <span className="text-xs text-foreground-600/60">Breakfast and lunch logged</span>
                        </div>
                      </div>
                      <div className="bg-accent-500/5 rounded-xl p-3 border border-accent-500/10">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-stethoscope-line text-accent-500 text-sm"></i>
                          <span className="text-xs font-semibold text-foreground-950">Upcoming</span>
                        </div>
                        <p className="text-xs text-foreground-600/60">Dr. Smith — August 25, 10:00 AM</p>
                      </div>
                      <div className="bg-accent-400/5 rounded-xl p-3 border border-accent-400/10">
                        <div className="flex items-center gap-2 mb-2">
                          <i className="ri-notification-3-line text-accent-400 text-sm"></i>
                          <span className="text-xs font-semibold text-foreground-950">Recent Alert</span>
                        </div>
                        <p className="text-xs text-foreground-600/60">Missed afternoon medication reminder</p>
                      </div>
                      <div className="mt-2 pt-3 border-t border-background-200/70">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-secondary-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold">S</div>
                          <div className="flex-1 bg-background-200/70 rounded-lg p-2">
                            <p className="text-xs text-foreground-700/80">&quot;Mom is doing well today. Had a good breakfast and a walk in the garden.&quot;</p>
                          </div>
                        </div>
                        <p className="text-[10px] text-foreground-600/40 mt-1 text-right">Sarah — Caregiver, 2h ago</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-1 w-24 bg-white/30 rounded-full mx-auto mt-2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}